import React, { useEffect, useState } from "react";
import Icon from "./Icon";
import { Button, Field } from "./ui";
import { apiGet, apiSend } from "../api";
import { C, R, S } from "../theme";

const card = { background: C.card, borderRadius: R.md, padding: S.lg, border: `1px solid ${C.border}`, marginBottom: S.md };

const BADGES = {
  live: { t: "LIVE", c: C.green },
  test: { t: "TEST", c: C.amber },
  unknown: { t: "SET", c: C.blue },
  none: { t: "NOT SET", c: C.red },
};

export default function StripeSetup() {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [pk, setPk] = useState("");
  const [sk, setSk] = useState("");
  const [ws, setWs] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [prices, setPrices] = useState(null);
  const [pbusy, setPbusy] = useState(false);
  const [sbusy, setSbusy] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");

  const load = async () => { try { setCfg(await apiGet("/admin/stripe-config")); } catch (e) { /* ignore */ } };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!pk && !sk && !ws) { setMsg("❌ Enter at least one key to save"); return; }
    setMsg(""); setBusy(true);
    try {
      const res = await apiSend("/admin/stripe-config", "POST", {
        publishable_key: pk || undefined,
        secret_key: sk || undefined,
        webhook_secret: ws || undefined,
      });
      if (res.validated === false) setMsg("⚠️ Saved, but Stripe rejected the secret key: " + (res.error || "invalid key"));
      else setMsg("✅ Keys saved" + (res.validated ? " & verified with Stripe" : ""));
      setPk(""); setSk(""); setWs("");
      await load();
      if (res.validated !== false) setTimeout(() => { setOpen(false); setMsg(""); }, 1500);
    } catch (e) {
      setMsg("❌ " + (e?.message || "Failed to save"));
    } finally { setBusy(false); }
  };

  const checkPrices = async () => {
    setPbusy(true); setPrices(null);
    try { setPrices(await apiGet("/admin/stripe-prices")); }
    catch (e) { setPrices({ error: e?.message || "Could not check prices" }); }
    finally { setPbusy(false); }
  };

  const syncPrices = async () => {
    setSbusy(true); setSyncMsg("");
    try {
      const r = await apiSend("/admin/stripe-sync-prices", "POST", {});
      const created = r.results.filter((x) => x.status === "created").length;
      const exists = r.results.filter((x) => x.status === "exists").length;
      const errs = r.results.filter((x) => x.status === "error");
      if (r.ok) setSyncMsg(`✅ Done in ${(r.mode || "").toUpperCase()} mode — ${created} created, ${exists} already existed. All 6 prices are ready.`);
      else setSyncMsg(`⚠️ ${errs.length} price(s) failed: ${errs.map((e) => `${e.lookup_key} (${e.error})`).join("; ")}`);
      await checkPrices();
    } catch (e) { setSyncMsg("❌ " + (e?.message || "Sync failed")); }
    finally { setSbusy(false); }
  };

  const badge = BADGES[cfg?.mode || "none"];

  return (
    <>
      <div style={{ color: C.text, fontSize: 18, fontWeight: 800, marginTop: S.md, marginBottom: S.sm }}>Stripe / Billing Setup</div>
      <div data-testid="stripe-setup-card" style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: S.md }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: C.card2, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="card" size={22} color={C.amber} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: C.text, fontSize: 16, fontWeight: 800 }}>Payment keys</div>
            <div style={{ color: C.muted, fontSize: 13, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis" }} data-testid="stripe-status-line">
              {cfg?.configured
                ? `Secret ${cfg.secret_key_masked} · webhook ${cfg.webhook_secret_set ? "set" : "not set"} · via ${cfg.source}`
                : "No Stripe keys configured yet"}
            </div>
          </div>
          {badge && <span data-testid="stripe-mode-badge" style={{ background: badge.c, color: C.black, fontWeight: 900, fontSize: 11, padding: "4px 8px", borderRadius: R.pill, whiteSpace: "nowrap" }}>{badge.t}</span>}
        </div>
        <div style={{ height: S.md }} />
        <Button title={cfg?.has_db_config ? "Update Stripe Keys" : "Configure Stripe Keys"} testID="open-stripe-config" onPress={() => { setOpen(true); setMsg(""); }} />
        <div style={{ height: S.sm }} />
        <button data-testid="check-prices" onClick={checkPrices} className="pressable" style={{ width: "100%", minHeight: 46, borderRadius: R.md, background: C.card2, border: `1px solid ${C.borderStrong}`, color: C.amber, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <Icon name="checkmark-done" size={18} color={C.amber} /> {pbusy ? "Checking your Stripe prices…" : "Verify Stripe prices"}
        </button>
        <div style={{ height: S.sm }} />
        <button data-testid="sync-prices" onClick={syncPrices} disabled={sbusy} className="pressable" style={{ width: "100%", minHeight: 46, borderRadius: R.md, background: C.amber, border: "none", color: C.black, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, opacity: sbusy ? 0.7 : 1 }}>
          <Icon name="build" size={18} color={C.black} /> {sbusy ? "Creating your 6 prices in Stripe…" : "Create / sync my 6 prices in Stripe"}
        </button>
        {!!syncMsg && <div data-testid="sync-result" style={{ color: syncMsg.startsWith("✅") ? C.green : syncMsg.startsWith("⚠️") ? C.amber : C.red, marginTop: S.sm, fontWeight: 700, fontSize: 13, lineHeight: 1.4 }}>{syncMsg}</div>}

        {prices && !prices.error && (
          <div data-testid="prices-result" style={{ marginTop: S.md }}>
            <div style={{ color: prices.all_found ? C.green : C.amber, fontSize: 13, fontWeight: 800, marginBottom: S.xs }}>
              {prices.all_found ? "✅ All 6 plan prices found in your Stripe account" : "⚠️ Some plan prices are missing — create them in Stripe with the lookup keys below"}
            </div>
            {prices.plans.map((p) => (
              <div key={p.plan_id} style={{ display: "flex", alignItems: "flex-start", gap: S.sm, padding: "7px 0", borderBottom: `1px solid ${C.border}` }}>
                <Icon name={p.found ? "checkmark-circle" : "close-circle"} size={18} color={p.found ? C.green : C.red} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: C.text, fontSize: 13, fontWeight: 700 }}>{p.label} <span style={{ color: C.muted, fontWeight: 600 }}>· £{p.expected_gbp}</span></div>
                  <div style={{ color: p.found ? (p.amount_matches ? C.muted : C.amber) : C.red, fontSize: 11, wordBreak: "break-all" }}>
                    lookup: <b>{p.lookup_key}</b>
                    {p.found
                      ? ` · ${p.currency} ${p.amount}/${p.interval} · ${p.price_id}${p.amount_matches === false ? " · ⚠️ amount differs from plan" : ""}`
                      : " · NOT FOUND in your account — add a recurring Price with this exact lookup key"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {prices?.error && <div data-testid="prices-error" style={{ color: C.red, marginTop: S.sm, fontWeight: 700, fontSize: 13 }}>{prices.error}</div>}
      </div>

      {open && (
        <div className="modal-backdrop modal-center" onClick={() => setOpen(false)}>
          <div data-testid="stripe-config-modal" onClick={(e) => e.stopPropagation()} style={{ background: C.card, borderRadius: R.lg, padding: S.xl, width: "100%", maxWidth: 440, border: `1px solid ${C.borderStrong}`, maxHeight: "88vh", overflowY: "auto" }}>
            <div className="display" style={{ color: C.text, fontSize: 22, fontWeight: 900, marginBottom: 4 }}>Stripe / Billing Setup</div>
            <div style={{ color: C.muted, fontSize: 13, marginBottom: S.lg, lineHeight: 1.45 }}>
              Paste your Stripe keys below. They're stored securely on the server and used for checkout, the billing portal and webhooks — overriding any platform environment values. Leave a field blank to keep the current value.
            </div>
            <Field label="Publishable key (pk_…)" testID="stripe-pk" autoCapitalize="none" value={pk} onChange={(e) => setPk(e.target.value)} placeholder={cfg?.publishable_key || "pk_live_…"} />
            <Field label="Secret key (sk_…)" testID="stripe-sk" autoCapitalize="none" value={sk} onChange={(e) => setSk(e.target.value)} placeholder={cfg?.secret_key_masked || "sk_live_…"} />
            <Field label="Webhook signing secret (whsec_…)" testID="stripe-ws" autoCapitalize="none" value={ws} onChange={(e) => setWs(e.target.value)} placeholder={cfg?.webhook_secret_set ? "•••• already set" : "whsec_…"} />
            {!!msg && (
              <div data-testid="stripe-config-msg" style={{ color: msg.startsWith("✅") ? C.green : msg.startsWith("⚠️") ? C.amber : C.red, marginBottom: S.md, fontWeight: 700, fontSize: 13, lineHeight: 1.4 }}>{msg}</div>
            )}
            <Button title="Save keys" testID="stripe-save" onPress={save} loading={busy} />
            <button className="pressable" onClick={() => setOpen(false)} style={{ display: "block", margin: "12px auto 0", color: C.muted, fontWeight: 700, background: "transparent", border: "none" }}>Close</button>
          </div>
        </div>
      )}
    </>
  );
}
